import { Injectable } from '@angular/core';
import { ApiService } from './api.service';

export interface CountryCoordinates {
  lat: number;
  lng: number;
  name: string;
  bounds?: {
    north: number;
    south: number;
    east: number;
    west: number;
  };
}

@Injectable({
  providedIn: 'root'
})
export class CountryCoordinatesService {

  // Cache for coordinates to avoid repeated API calls
  private coordinatesCache: { [key: string]: CountryCoordinates } = {};

  // Default coordinates for India (fallback only)
  private readonly defaultCoordinates: CountryCoordinates = {
    lat: 20.5937,
    lng: 78.9629,
    name: 'India',
    bounds: {
      north: 35.5133,
      south: 6.4627,
      east: 97.3956,
      west: 68.1097
    }
  };

  constructor(private apiService: ApiService) { }

  /**
   * Get coordinates for a specific country code
   * @param countryCode - ISO country code (e.g., 'IN', 'US', 'GB')
   * @returns CountryCoordinates object or default (India) if not found
   */
  getCoordinates(countryCode: string): CountryCoordinates {
    // First check cache
    if (this.coordinatesCache[countryCode]) {
      return this.coordinatesCache[countryCode];
    }

    // If not found, try to get coordinates dynamically
    this.getCoordinatesFromAPI(countryCode).then(coords => {
      if (coords) {
        this.coordinatesCache[countryCode] = coords;

      }
    }).catch(error => {
      console.warn(`Failed to load coordinates for ${countryCode}:`, error);
    });

    // Return default coordinates while API call is in progress
    return this.defaultCoordinates;
  }

  /**
   * Get coordinates asynchronously (recommended for new coordinates)
   * @param countryCode - ISO country code
   * @returns Promise with coordinates
   */
  async getCoordinatesAsync(countryCode: string): Promise<CountryCoordinates> {
    // First check cache
    if (this.coordinatesCache[countryCode]) {
      return this.coordinatesCache[countryCode];
    }

    // Try to get coordinates from API
    const coords = await this.getCoordinatesFromAPI(countryCode);
    if (coords) {
      this.coordinatesCache[countryCode] = coords;
      return coords;
    }

    // Return default if not found
    return this.defaultCoordinates;
  }

  /**
   * Get all available countries with their coordinates
   * @returns Array of country objects with code and name
   */
  getAvailableCountries(): Array<{ code: string, name: string }> {
    // Return only cached coordinates (dynamically loaded)
    return Object.entries(this.coordinatesCache).map(([code, data]) => ({
      code,
      name: data.name
    }));
  }

  /**
   * Get country name from country code
   * @param countryCode - ISO country code
   * @returns Country name or 'India' as default
   */
  getCountryName(countryCode: string): string {
    const coords = this.getCoordinates(countryCode);
    return coords.name;
  }

  /**
   * Check if a country code is supported
   * @param countryCode - ISO country code
   * @returns true if country is supported, false otherwise
   */
  isCountrySupported(countryCode: string): boolean {
    return countryCode in this.coordinatesCache;
  }

  /**
   * Get default coordinates (India)
   * @returns Default country coordinates
   */
  getDefaultCoordinates(): CountryCoordinates {
    return this.defaultCoordinates;
  }

  /**
   * Get coordinates from Google Geocoding API
   * @param countryCode - ISO country code
   * @returns Promise with coordinates or null if not found
   */
  private async getCoordinatesFromAPI(countryCode: string): Promise<CountryCoordinates | null> {
    try {
      const apiKey = this.apiService.getGoogleMapsApiKey();
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?components=country:${countryCode}&key=${apiKey}`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.status !== 'OK' || !data.results || data.results.length === 0) {
        return null;
      }

      const result = data.results[0];

      if (
        !result?.geometry?.location ||
        typeof result.geometry.location.lat !== 'number' ||
        typeof result.geometry.location.lng !== 'number'
      ) {
        return null;
      }

      const countryComponent = result.address_components?.find(
        (comp: any) => comp.types?.includes('country')
      );

      // Country bounds that cross the antimeridian (US, Russia, Fiji) have
      // west > east. Google Maps strictBounds then centers the map in the ocean
      // instead of the selected place. Viewport is a non-wrapping rectangle.
      const rawBounds = result.geometry?.bounds;
      const viewport = result.geometry?.viewport;
      const crossesAntimeridian = !!rawBounds
        && rawBounds.southwest.lng > rawBounds.northeast.lng;
      const bounds = (crossesAntimeridian ? viewport : rawBounds) || viewport || rawBounds;
      const boundsObj = bounds ? {
        north: bounds.northeast.lat,
        south: bounds.southwest.lat,
        east: bounds.northeast.lng,
        west: bounds.southwest.lng
      } : undefined;
      const safeBounds = boundsObj && this.isSafeMapBounds(boundsObj) ? boundsObj : undefined;

      return {
        lat: result.geometry.location.lat,
        lng: result.geometry.location.lng,
        name: countryComponent?.long_name ||
              countryComponent?.short_name ||
              'Unknown Country',
        bounds: safeBounds
      };
    } catch (error) {
      console.warn(`Failed to fetch coordinates for country ${countryCode}:`, error);
      return null;
    }
  }

  /**
   * Preload coordinates for multiple countries
   * @param countryCodes - Array of country codes to preload
   */
  async preloadCoordinates(countryCodes: string[]): Promise<void> {
    const promises = countryCodes.map(async (code) => {
      if (!this.coordinatesCache[code]) {
        const coords = await this.getCoordinatesFromAPI(code);
        if (coords) {
          this.coordinatesCache[code] = coords;
        }
      }
    });

    await Promise.all(promises);
  }

  /**
   * Clear coordinates cache
   */
  clearCache(): void {
    this.coordinatesCache = {};
  }

  /**
   * Get cache statistics
   */
  getCacheStats(): { cached: number } {
    return {
      cached: Object.keys(this.coordinatesCache).length
    };
  }

  /** Bounds that wrap the date line make Google Maps snap the camera into the ocean. */
  isSafeMapBounds(bounds?: { north: number; south: number; east: number; west: number }): boolean {
    if (!bounds) return false;
    return bounds.west < bounds.east && bounds.south < bounds.north;
  }

  /**
   * Lock a map to a country only when the box does not cross the antimeridian.
   * Every map in the app should use this instead of setting strictBounds itself.
   */
  async applyCountryMapRestriction(mapInstance: any, countryCode: string): Promise<void> {
    const maps = (globalThis as any).google?.maps;
    if (!mapInstance || !maps) return;

    try {
      const coords = await this.getCoordinatesAsync(countryCode);
      const box = coords?.bounds;
      if (!this.isSafeMapBounds(box) || !box) {
        mapInstance.setOptions({ restriction: null });
        return;
      }

      const bounds = new maps.LatLngBounds(
        new maps.LatLng(box.south, box.west),
        new maps.LatLng(box.north, box.east)
      );

      const center = mapInstance.getCenter?.();
      if (!center || !bounds.contains(center)) {
        mapInstance.setCenter({ lat: coords.lat, lng: coords.lng });
      }

      // strictBounds true makes Google Maps show "Timeout has occurred"
      // when the camera starts outside the box or the country is very wide.
      mapInstance.setOptions({
        restriction: {
          latLngBounds: bounds,
          strictBounds: false
        }
      });
    } catch (error) {
      console.warn('Failed to apply country map restriction:', error);
      try {
        mapInstance.setOptions({ restriction: null });
      } catch {
        // map may already be destroyed
      }
    }
  }
}
